import { Injectable, Signal, inject, signal } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { HdCircuitInfoService } from '../hd-circuit-info.service';
import { HdGateInfoService } from '../hd-gate-info.service';
import { HdContentLanguageService } from '../hd-content-language.service';
import { HdDocRef, docPanelKey } from './hd-doc-ref.model';
import { HD_DOC_LINK_OPEN_BEHAVIOR_DEFAULT, HdDocLinkOpenBehavior } from './hd-doc-link-open-behavior';
import { HdDocViewerPanelComponent } from './hd-doc-viewer-panel.component';

/** Breedte-modus voor zwevende doc-dialogs (propageert doorheen navigatie). */
export type HdDocDialogSize = 'normal' | 'wide';

/**
 * Centrale toegang tot de gedockte **doc-viewer** panelen. Wordt gebruikt door:
 *  - de configurator (info-iconen van circuits, stromen en het overzicht) om
 *    een paneel te openen;
 *  - de {@link HdDocViewerComponent} zelf om — in `new-panel`-modus — een
 *    doorverwezen link in een apart paneel te openen.
 *
 * De service houdt de actieve {@link HdDocLinkOpenBehavior} bij (gevoed vanuit
 * de globale chart-settings) en leidt per {@link HdDocRef} de paneel-`key` en
 * de dock-tab-label af. Panelen docken — net als de circuit-info-panelen —
 * meteen in de `'full'`-zone.
 */
@Injectable({ providedIn: 'root' })
export class HdDocPanelService {
  private readonly floatingPanel = inject(FloatingPanelService);
  private readonly circuitInfoService = inject(HdCircuitInfoService);
  private readonly gateInfoService = inject(HdGateInfoService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  private readonly linkOpenModeSignal = signal<HdDocLinkOpenBehavior>(HD_DOC_LINK_OPEN_BEHAVIOR_DEFAULT);

  /** Actieve link-open-modus (reactief). Gezet door de chart-pagina uit de settings. */
  readonly linkOpenMode: Signal<HdDocLinkOpenBehavior> = this.linkOpenModeSignal.asReadonly();

  setLinkOpenMode(mode: HdDocLinkOpenBehavior): void {
    this.linkOpenModeSignal.set(mode);
  }

  private readonly dialogSizeSignal = signal<HdDocDialogSize>('normal');

  /**
   * Breedte-modus voor zwevende doc-dialogs. Globaal (zoals {@link linkOpenMode})
   * zodat de gekozen breedte doorheen navigatie meegaat: kies je normale dialogs
   * dan navigeer je door normale, vergroot je er één dan navigeer je door grote.
   */
  readonly dialogSize: Signal<HdDocDialogSize> = this.dialogSizeSignal.asReadonly();

  setDialogSize(size: HdDocDialogSize): void {
    this.dialogSizeSignal.set(size);
  }

  toggleDialogSize(): void {
    this.dialogSizeSignal.update(size => (size === 'wide' ? 'normal' : 'wide'));
  }

  /**
   * Opent (of focust — dedup op de doc-key) een gedockt doc-viewer-paneel voor
   * de gegeven ref. Bestaand paneel met dezelfde bron komt naar voren i.p.v.
   * een tweede te openen; de eigen history-stack van dat paneel blijft intact.
   */
  openDocPanel(ref: HdDocRef): void {
    this.floatingPanel.openOrFocus<HdDocViewerPanelComponent, HdDocRef>({
      key: docPanelKey(ref),
      component: HdDocViewerPanelComponent,
      data: ref,
      dockable: true,
      dockTab: { label: this.docTabLabel(ref) },
      dockZone: 'full',
      openInitiallyDocked: true,
      redockOnReopen: true,
    });
  }

  /** Menselijke label voor de dock-tab van een doc-ref (actieve app-taal). */
  docTabLabel(ref: HdDocRef): string {
    const lang = this.contentLang.effectiveLang();
    switch (ref.kind) {
      case 'circuit': {
        const translation = this.circuitInfoService.circuitTranslation(ref.circuitKey);
        return translation.shortName || translation.name || ref.circuitKey;
      }
      case 'stream':
        return this.gateInfoService.streamNameIn(ref.streamKey, lang);
      case 'channel': {
        const translation = this.gateInfoService.channelTranslationIn(ref.channelKey, lang);
        return translation?.name || ref.channelKey;
      }
      case 'overview':
        return this.transloco.translate('humanDesign.chart.configurator.circuitsOverviewTab', undefined, lang);
      case 'channels-overview':
        return this.transloco.translate('humanDesign.chart.channelInfo.allChannels.tab', undefined, lang);
      case 'markdown':
        return ref.title ?? this.transloco.translate('humanDesign.chart.configurator.docPanel.documentTab', undefined, lang);
    }
  }
}
